/**
 * Detects whether the first row of a CSV file appears to be column headers
 * or actual student data records.
 */
export function detectHeaderRow(firstLine: string[]): boolean {
  if (!firstLine || firstLine.length === 0) return true;

  // If any cell contains an '@' sign, it is almost certainly a student email address
  const hasEmail = firstLine.some((f) => f.includes('@') && /\S+@\S+\.\S+/.test(f.trim()));
  if (hasEmail) return false;

  // If any cell is a pure 5-9 digit number, it is almost certainly a student matric number
  const hasMatricNumber = firstLine.some((f) => /^\d{5,9}$/.test(f.trim()));
  if (hasMatricNumber) return false;

  // Check for common column title keywords (whole words or clean matches)
  const headerKeywords = [
    'matric',
    'matno',
    'regno',
    'student',
    'voter',
    'name',
    'fullname',
    'studentname',
    'email',
    'mail',
    'level',
    'gender',
    'sex',
    'hall',
    'department',
    'dept',
    'faculty',
    'phone',
    'amount',
    'reference',
    'status',
    'password',
    'code',
  ];

  const matchesKeyword = firstLine.some((f) => {
    const clean = f.toLowerCase().replace(/[^a-z0-9]/g, '');
    return headerKeywords.some((kw) => clean === kw || clean.startsWith(kw) || clean.endsWith(kw));
  });

  return matchesKeyword;
}

/**
 * Robust CSV parser that correctly handles:
 * - quoted values containing commas
 * - escaped double quotes ("")
 * - variable line endings (\r\n and \n)
 * - optional header row toggle and smart header detection
 */
export function parseCsvText(
  text: string,
  options?: { hasHeaderRow?: boolean }
): {
  headers: string[];
  rows: string[][];
  detectedHasHeaders: boolean;
  rawLinesCount: number;
} {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    return { headers: [], rows: [], detectedHasHeaders: true, rawLinesCount: 0 };
  }

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        fields.push(cur.trim());
        cur = '';
      } else {
        cur += char;
      }
    }
    fields.push(cur.trim());
    return fields;
  };

  const parsedLines = lines.map(parseLine).filter((row) => row.some((p) => p.length > 0));
  if (parsedLines.length === 0) {
    return { headers: [], rows: [], detectedHasHeaders: true, rawLinesCount: 0 };
  }

  const detectedHasHeaders = detectHeaderRow(parsedLines[0]);
  const hasHeaders = options?.hasHeaderRow !== undefined ? options.hasHeaderRow : detectedHasHeaders;

  if (hasHeaders) {
    const headers = parsedLines[0].map((h, idx) => h.trim() || `Col ${idx + 1}`);
    const rows = parsedLines.slice(1);
    return { headers, rows, detectedHasHeaders, rawLinesCount: parsedLines.length };
  } else {
    // No headers: include line 0 in rows and synthesize friendly column labels with sample values
    const firstRow = parsedLines[0];
    const headers = firstRow.map((val, idx) => {
      const sample = val.length > 18 ? val.slice(0, 18) + '...' : val;
      return sample ? `Col ${idx + 1}: ${sample}` : `Col ${idx + 1}`;
    });
    const rows = parsedLines;
    return { headers, rows, detectedHasHeaders, rawLinesCount: parsedLines.length };
  }
}

export interface ColumnMapping {
  matric: number;
  name: number;
  email: number;
  password: number;
  level: number;
  gender: number;
  hall: number;
  department: number;
}

/**
 * Automatically inspects CSV headers and sample row values to match them to standard voter attributes.
 */
export function autoMatchColumns(headers: string[], sampleRow?: string[]): ColumnMapping {
  const cleanHeaders = headers.map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const mapping: ColumnMapping = {
    matric: -1,
    name: -1,
    email: -1,
    password: -1,
    level: -1,
    gender: -1,
    hall: -1,
    department: -1,
  };

  // 1. Try matching based on header label text
  cleanHeaders.forEach((h, idx) => {
    if (
      mapping.matric === -1 &&
      (h.includes('matric') || h.includes('matno') || h.includes('regno') || h.includes('studentid') || h === 'id')
    ) {
      mapping.matric = idx;
    } else if (
      mapping.name === -1 &&
      (h.includes('fullname') || h.includes('studentname') || h.includes('votername') || h === 'name')
    ) {
      mapping.name = idx;
    } else if (mapping.email === -1 && (h.includes('email') || h.includes('mail'))) {
      mapping.email = idx;
    } else if (
      mapping.password === -1 &&
      (h.includes('pass') || h.includes('pwd') || h.includes('accesscode') || h.includes('pin') || h.includes('secret'))
    ) {
      mapping.password = idx;
    } else if (mapping.level === -1 && (h.includes('level') || h.includes('year') || h.includes('class') || h.includes('cohort'))) {
      mapping.level = idx;
    } else if (mapping.gender === -1 && (h.includes('gender') || h.includes('sex'))) {
      mapping.gender = idx;
    } else if (mapping.hall === -1 && (h.includes('hall') || h.includes('hostel') || h.includes('residence'))) {
      mapping.hall = idx;
    } else if (mapping.department === -1 && (h.includes('dept') || h.includes('department') || h.includes('faculty'))) {
      mapping.department = idx;
    }
  });

  // 2. If headers did not match (e.g. headerless data files), inspect the sample row values
  if (sampleRow && sampleRow.length > 0) {
    sampleRow.forEach((val, idx) => {
      const trimmed = val.trim();
      if (!trimmed) return;

      if (mapping.email === -1 && trimmed.includes('@') && /\S+@\S+\.\S+/.test(trimmed)) {
        mapping.email = idx;
      } else if (mapping.matric === -1 && /^\d{5,9}$/.test(trimmed)) {
        mapping.matric = idx;
      } else if (mapping.level === -1 && /^(100|200|300|400|500|600)l?$/i.test(trimmed)) {
        mapping.level = idx;
      } else if (mapping.gender === -1 && /^(male|female|m|f)$/i.test(trimmed)) {
        mapping.gender = idx;
      }
    });

    // Match name from remaining string field with spaces / alphabet
    if (mapping.name === -1) {
      sampleRow.forEach((val, idx) => {
        const trimmed = val.trim();
        if (
          idx !== mapping.email &&
          idx !== mapping.matric &&
          idx !== mapping.level &&
          idx !== mapping.gender &&
          /^[A-Za-z\s'-]{3,}$/.test(trimmed)
        ) {
          mapping.name = idx;
        }
      });
    }
  }

  // Fallbacks if not detected by naming
  if (mapping.matric === -1 && headers.length > 0) mapping.matric = 0;
  if (mapping.name === -1 && headers.length > 1) mapping.name = 1;

  return mapping;
}
